import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Echo from '../Sockect/Echo';
import { toast } from 'react-toastify';
import { downloadFileWithAuth, getDownloadFilename } from '../../Utils/downloadFileWithAuth';
// import logger from '../../Utils/logger';
import logger from "../../Utils/logger";

const PENDING_KEY = 'payroll_download_pending';
const EVENT_NAME = '.PayrollReportReady';

function getEventUrl(event) {
	return event?.url || event?.data?.url || '';
}

function getEventExportId(event) {
	return event?.export_id || event?.data?.export_id || '';
}

function getPendingExportId() {
	try {
		const pending = sessionStorage.getItem(PENDING_KEY);
		if (!pending) {
			return '';
		}

		const parsed = JSON.parse(pending);
		return parsed?.export_id || '';
	} catch (e) {
		return '';
	}
}

const PayrollReportDownloadListener = () => {
	const location = useLocation();
	const currentChannelNameRef = useRef(null);
	const currentChannelRef = useRef(null);
	const handleEventRef = useRef(null);

	// Only detach our listener — never Echo.leave the shared user.* channel
	// (other report downloads also use it).
	const detachListener = useCallback(() => {
		const channel = currentChannelRef.current;
		try {
			channel?.stopListening?.('PayrollReportReady');
			channel?.stopListening?.(EVENT_NAME);
		} catch (e) {}
	}, []);

	const handleDownloadReady = useCallback(async (event) => {
		const url = getEventUrl(event);
		if (!url) {
			toast.error('Download link was not received. Please try again.');
			return;
		}

		try {
			await downloadFileWithAuth(url, getDownloadFilename(event, url, 'payroll-report.xlsx'));
			toast.success('Payroll report is Downloaded');
			window.dispatchEvent(new CustomEvent('payroll:download:started'));
			try {
				sessionStorage.removeItem(PENDING_KEY);
			} catch (e) {}
		} catch (e) {
			logger.error('Payroll download failed:', e);
			toast.error('Failed to start download');
		}
	}, []);

	handleEventRef.current = (event) => {
		const pendingExportId = getPendingExportId();
		const eventExportId = getEventExportId(event);
		if (!pendingExportId || !eventExportId || pendingExportId !== eventExportId) {
			return;
		}
		handleDownloadReady(event);
	};

	const subscribe = useCallback(() => {
		const userId = localStorage.getItem('user_id');
		const token = localStorage.getItem('token');

		if (!userId || !token) {
			detachListener();
			currentChannelNameRef.current = null;
			currentChannelRef.current = null;
			return;
		}

		const channelName = `user.${userId}`;

		// Always re-join via Echo.private so we recover if another feature
		// previously called Echo.leave on this shared channel.
		let channel;
		try {
			detachListener();
			channel = Echo.private(channelName);
			currentChannelNameRef.current = channelName;
			currentChannelRef.current = channel;
		} catch (e) {
			return;
		}

		try {
			channel.listen(EVENT_NAME, (event) => handleEventRef.current?.(event));
		} catch (e) {}
	}, [detachListener]);

	useEffect(() => {
		subscribe();
	}, [subscribe, location.pathname]);

	useEffect(() => {
		const onReconnected = () => subscribe();
		window.addEventListener('socket:reconnected', onReconnected);
		return () => window.removeEventListener('socket:reconnected', onReconnected);
	}, [subscribe]);

	useEffect(() => () => detachListener(), [detachListener]);

	return null;
};

export default PayrollReportDownloadListener;
